import type { SessionResponse } from '@chatapp-inbox/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  ApiError,
  fetchSession,
  logout as requestLogout,
  setUnauthorizedHandler,
  shouldRetry,
} from '../api';
import { AuthContext, type AuthContextValue } from './context';
import { createFlagsStore } from './flags-store';
import { describeLogoutError } from './messages';
import { SESSION_KEY, type SessionState } from './session';
import { endSession, startSession } from './session-actions';
import { useSessionReconciliation } from './use-session-reconciliation';

const unknownSessionError = (): ApiError =>
  new ApiError(0, 'unknown', 'Não foi possível verificar sua sessão. Tente de novo.');

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [store] = useState(createFlagsStore);
  const flags = useSyncExternalStore(store.subscribe, store.get);

  const query = useQuery<SessionResponse | null>({
    queryKey: SESSION_KEY,
    queryFn: () => fetchSession(),
    // A sessão só muda por ação nossa (login, logout, 401), nunca "por envelhecer": consultar de
    // novo ao focar a janela só geraria piscadas e pedidos à toa.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: shouldRetry,
  });
  const { refetch } = query;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Sem sessão (ainda carregando, ou já encerrada) não há o que expirar. Isto também torna o
      // tratador idempotente quando várias requisições recebem 401 juntas, e impede que um 401
      // tardio de uma requisição da sessão que acabou de sair seja confundido com "expirou".
      if (!queryClient.getQueryData(SESSION_KEY)) return;
      // O 401 de quem está saindo é esperado: o servidor já apagou a sessão.
      if (store.get().loggingOut) return;
      endSession(queryClient, store, 'expired');
    });
    // O React em modo estrito monta, desmonta e monta de novo: o cleanup precisa desfazer
    // exatamente o que o efeito fez para o segundo registro valer.
    return () => setUnauthorizedHandler(null);
  }, [queryClient, store]);

  // As abas dividem o cookie de sessão: outra aba pode ter saído, entrado ou trocado de conta.
  useSessionReconciliation(store);

  const signedIn = useCallback(
    (session: SessionResponse) => startSession(queryClient, store, session),
    [queryClient, store],
  );

  const logout = useCallback(() => {
    // Lê do store (síncrono), não do estado renderizado: dois cliques antes do re-render contam como um.
    if (store.get().loggingOut) return;
    store.set({ loggingOut: true, logoutError: null });
    requestLogout().then(
      // `loggingOut` fica true de propósito: o botão não pode "voltar" por um instante antes de
      // a tela trocar. Quem consome só o vê enquanto há sessão (veja `pending` abaixo).
      () => endSession(queryClient, store, 'logout'),
      (err: unknown) => store.set({ loggingOut: false, logoutError: describeLogoutError(err) }),
    );
  }, [queryClient, store]);

  const state = useMemo<SessionState>(() => {
    if (query.data) return { status: 'authenticated', user: query.data.user };
    if (query.data === null) {
      return { status: 'unauthenticated', expired: flags.endReason === 'expired' };
    }
    if (query.isError) {
      const error = query.error instanceof ApiError ? query.error : unknownSessionError();
      return { status: 'error', error, retry };
    }
    return { status: 'loading' };
  }, [query.data, query.isError, query.error, flags.endReason, retry]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      endReason: flags.endReason,
      logoutStatus: {
        pending: flags.loggingOut && state.status === 'authenticated',
        error: flags.logoutError,
      },
      signedIn,
      logout,
    }),
    [state, flags.endReason, flags.loggingOut, flags.logoutError, signedIn, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
